import React, { useContext } from "react";
import { captureException } from "@sentry/browser";
import { omit } from "lodash";
import { useDropzone } from "react-dropzone";
import { Path, useFormContext, UseFormReturn } from "react-hook-form";
import { MIME_TYPE } from "common/constants";
import { useGetPaginatedAccountDocumentTemplatesQuery } from "../../../../generated/graphql";
import { useMultipartFileUpload } from "../../../../hooks/useMultipartFileUpload";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { AuthContext } from "../../../Authorization/AuthContext";
import { ReactHookFormMultiSelect } from "../../../Inputs/MultiSelect";
import { Select, SingleFileUpload } from "../../../Inputs/react-hook-form";

type InputFileProps = {
  name: string;
  label?: string;
  description?: string;
  required?: boolean;
  allowedMimeTypes: MIME_TYPE[];
};

export type BaseDispatchForm = {
  inputCsv: { blob: File };
  inputZip: { blob: File };
};
export type BulkDocumentGenerationForm = {
  propertiesFile: { blob: File };
  overridesFile: { blob: File };
  labelMappingsFile: { blob: File };
};

export const AccountCustomMapSelect = ({
  description,
  required,
}: {
  description?: string;
  required?: boolean;
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<{ customMapId?: string }>();

  const { account } = useContext(AuthContext);

  const customMapOptions =
    account?.customMaps?.map((customMap: any) => ({
      label: customMap.name,
      value: customMap.id,
    })) ?? [];

  return (
    <Select
      control={control}
      name="customMapId"
      label="Custom map"
      options={customMapOptions}
      error={errors.customMapId?.message}
      description={description}
      required={required}
    />
  );
};

export const AccountDocumentTemplateSelect = ({
  description,
  required,
}: {
  description?: string;
  required?: boolean;
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<{ templateId?: string }>();

  const { account } = useContext(AuthContext);

  const { data, loading } = useGetPaginatedAccountDocumentTemplatesQuery({
    variables: {
      accountId: account!.id,
      page: 1,
      pageSize: 100,
    },
  });

  const templateOptions =
    data?.getPaginatedAccountDocumentTemplates?.data?.map((template: any) => ({
      label: template.name,
      value: template.id,
    })) ?? [];

  return (
    <Select
      control={control}
      name="templateId"
      label="Document template"
      options={templateOptions}
      error={errors.templateId?.message}
      description={description}
      required={required}
      disabled={loading}
    />
  );
};

export const AccountDocumentTypeSelect = ({
  description,
}: {
  description?: string;
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<{ accountDocumentTypeId?: string }>();

  const { account } = useContext(AuthContext);

  const accountDocumentTypeOptions =
    account?.accountDocumentTypes.map(accountDocumentType => ({
      label: accountDocumentType.name,
      value: accountDocumentType.id,
    })) ?? [];

  return (
    <Select
      control={control}
      name="accountDocumentTypeId"
      label="Account Document Type"
      options={accountDocumentTypeOptions}
      error={errors.accountDocumentTypeId?.message}
      description={description}
    />
  );
};

export const AccountPropertyWarningDefinitionSelect = ({
  description,
}: {
  description?: string;
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<{ accountPropertyWarningDefinitionIds?: string[] }>();

  const { account } = useContext(AuthContext);

  const accountPropertyWarningDefinitionOptions =
    account?.accountPropertyWarningDefinitions.map(definition => ({
      label: definition.title,
      value: definition.id,
    })) ?? [];

  return (
    <ReactHookFormMultiSelect
      control={control}
      name="accountPropertyWarningDefinitionIds"
      label="Property warning definitions"
      options={accountPropertyWarningDefinitionOptions}
      error={errors.accountPropertyWarningDefinitionIds?.message}
      helperText={description}
    />
  );
};

export const InputFile = ({
  name,
  label,
  description,
  required,
  allowedMimeTypes,
}: InputFileProps) => {
  const { control, register } = useFormContext();

  return (
    <div>
      <SingleFileUpload
        {...omit(register(name), "ref")}
        control={control}
        allowedMimeTypes={allowedMimeTypes}
        name={name}
        useDropzone={useDropzone}
        required={required}
        label={label}
        description={description}
        rules={{
          ...(required ?
            {
              required: "This field is required",
            }
          : {}),
        }}
      />
    </div>
  );
};

export const useDispatchFileUpload = <T extends BaseDispatchForm>({
  getValues,
}: UseFormReturn<T, any>) => {
  const { addErrorToast } = useStatusToasts();

  const [uploadSourceFile, { loading }] = useMultipartFileUpload({
    onError: () => addErrorToast("Failed to upload input file"),
  });

  const uploadFiles = async () => {
    const csvPromise: Promise<Maybe<string>> = new Promise(resolve =>
      uploadSourceFile({
        file: getValues("inputCsv.blob" as Path<T>) as File,
        onCompleted: key => {
          resolve(key);
        },
        onUploadError: e => {
          captureException(e);
          resolve(null);
        },
      }),
    );

    const zipBlobValue = getValues("inputZip.blob" as Path<T>);
    const zipPromise: Promise<Maybe<string>> = new Promise(resolve => {
      if (!zipBlobValue) {
        resolve(null);
        return;
      }
      uploadSourceFile({
        file: getValues("inputZip.blob" as Path<T>) as File,
        onCompleted: key => {
          resolve(key);
        },
        onUploadError: e => {
          captureException(e);
          resolve(null);
        },
      });
    });

    const [inputCsvKey, inputZipKey] = await Promise.all([
      csvPromise,
      zipPromise,
    ]);

    if (!inputCsvKey) {
      addErrorToast("Failed to upload csv file, please reload and try again.");
      return;
    }

    if (zipBlobValue && !inputZipKey) {
      addErrorToast("Failed to upload zip file, please reload and try again.");
      return;
    }

    return {
      inputCsvKey,
      inputZipKey,
    };
  };

  return { uploadFiles, isUploading: loading };
};

export const useDispatchBulkDocumentGenerationFileUpload = <
  T extends BulkDocumentGenerationForm,
>({
  getValues,
}: UseFormReturn<T, any>) => {
  const { addErrorToast } = useStatusToasts();

  const [uploadSourceFile, { loading }] = useMultipartFileUpload({
    onError: () => addErrorToast("Failed to upload input file"),
  });

  const uploadFiles = async () => {
    const propertiesFileBlob = getValues(
      "propertiesFile.blob" as Path<T>,
    ) as File;
    const propertiesPromise: Promise<Maybe<string>> =
      propertiesFileBlob ?
        new Promise(resolve =>
          uploadSourceFile({
            file: propertiesFileBlob,
            onCompleted: key => {
              resolve(key);
            },
            onUploadError: e => {
              captureException(e);
              resolve(null);
            },
          }),
        )
      : Promise.resolve(null);

    const overridesFileBlob = getValues(
      "overridesFile.blob" as Path<T>,
    ) as File;
    const overridesPromise: Promise<Maybe<string>> =
      overridesFileBlob ?
        new Promise(resolve =>
          uploadSourceFile({
            file: overridesFileBlob,
            onCompleted: key => {
              resolve(key);
            },
            onUploadError: e => {
              captureException(e);
              resolve(null);
            },
          }),
        )
      : Promise.resolve(null);

    const labelMappingsFileBlob = getValues(
      "labelMappingsFile.blob" as Path<T>,
    ) as File;
    const labelMappingsPromise: Promise<Maybe<string>> =
      labelMappingsFileBlob ?
        new Promise(resolve =>
          uploadSourceFile({
            file: labelMappingsFileBlob,
            onCompleted: key => {
              resolve(key);
            },
            onUploadError: e => {
              captureException(e);
              resolve(null);
            },
          }),
        )
      : Promise.resolve(null);

    const [propertiesFileKey, overridesFileKey, labelMappingsFileKey] =
      await Promise.all([
        propertiesPromise,
        overridesPromise,
        labelMappingsPromise,
      ]);

    if (propertiesFileBlob && !propertiesFileKey) {
      addErrorToast(
        "Failed to upload properties file, please reload and try again.",
      );
      return;
    }

    if (overridesFileBlob && !overridesFileKey) {
      addErrorToast(
        "Failed to upload overrides file, please reload and try again.",
      );
      return;
    }

    if (labelMappingsFileBlob && !labelMappingsFileKey) {
      addErrorToast(
        "Failed to upload label mappings file, please reload and try again.",
      );
      return;
    }

    return {
      propertiesFileKey,
      overridesFileKey,
      labelMappingsFileKey,
    };
  };

  return { uploadFiles, isUploading: loading };
};
